import axiosInstance from '../axiosInstance'

// Models
import { SiteID } from '../sites/models/site'
import { EmployeeID } from '../employees/models/employee'
import { EmployeeShiftOffer, EmployeeShiftOfferDto, employeeShiftOfferFromDto, ShiftOfferStatus } from './models/employeeShiftOffer'
import { Pagination } from '../common/models/pagination'
import { ShiftID } from '../shifts/models/shift'
import { AreaID } from '../areas/models/area'
import { DateTime } from 'luxon'
import { SkillID } from '../skills/models/skill'

export interface EmployeeShiftOfferSearchForm {
    dateFrom?: DateTime
    dateTo?: DateTime

    status?: string

    areaId?: AreaID
    skillId?: SkillID
    name?: string
    perPage: number
    page: number
}

interface EmployeeShiftOfferSearchParams {
    area_id?: AreaID
    skill_id?: SkillID
    start_date: string
    end_date: string
    status?: string
    name?: string
    per_page: number
    page: number
    includes?: ('employee' | 'shift' | 'shift.area')[]
}

export const fetch = async (siteId: SiteID, shiftId: ShiftID, form: EmployeeShiftOfferSearchForm): Promise<{ offers: EmployeeShiftOffer[], pagination: Pagination }> => {
    const params: EmployeeShiftOfferSearchParams = {
        start_date: '2024-01-01',
        end_date: '2024-12-31',
        per_page: form.perPage,
        page: form.page,
    }

    const name = form.name?.trim() ?? ''
    if (name.length > 0) {
        params.name = name
    }

    if (form.areaId) {
        params.area_id = form.areaId
    }

    if (form.skillId) {
        params.skill_id = form.skillId
    }

    const response = await axiosInstance.get(`api/orgs/${siteId}/shifts/${shiftId}/offers`, { params })

    const data = response.data.data as EmployeeShiftOfferDto[]

    return {
        offers: data.map((offer) => {
            return employeeShiftOfferFromDto(offer)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}

export const add = async (siteId: SiteID, shiftId: ShiftID, employeeId: EmployeeID): Promise<void> => {
    await axiosInstance.post(`api/orgs/${siteId}/shifts/${shiftId}/offers/${employeeId}`)
}

export const remove = async (siteId: SiteID, shiftId: ShiftID, employeeId: EmployeeID): Promise<void> => {
    await axiosInstance.delete(`api/orgs/${siteId}/shifts/${shiftId}/offers/${employeeId}`)
}

export interface EmployeeShiftOfferUpdateForm {
    status: ShiftOfferStatus
}

interface EmployeeShiftOfferUpdateData {
    status: ShiftOfferStatus
}

export const update = async (siteId: SiteID, shiftId: ShiftID, employeeId: EmployeeID, form: EmployeeShiftOfferUpdateForm): Promise<void> => {
    const data: EmployeeShiftOfferUpdateData = {
        status: form.status,
    }

    await axiosInstance.patch(`api/orgs/${siteId}/shifts/${shiftId}/offers/${employeeId}`, data)
}

export const fetchByEmployee = async (siteId: SiteID, employeeId: EmployeeID, form: EmployeeShiftOfferSearchForm): Promise<{ offers: EmployeeShiftOffer[], pagination: Pagination }> => {
    const params: EmployeeShiftOfferSearchParams = {
        start_date: form.dateFrom?.toISODate() ?? '2024-01-01',
        end_date: form.dateTo?.toISODate() ?? '2024-12-31',
        per_page: form.perPage,
        page: form.page,
    }

    const name = form.name?.trim() ?? ''
    if (name.length > 0) {
        params.name = name
    }

    if (form.status) {
        params.status = form.status
    }

    if (form.areaId) {
        params.area_id = form.areaId
    }

    if (form.skillId) {
        params.skill_id = form.skillId
    }

    params.includes = ['shift.area']

    const response = await axiosInstance.get(`api/orgs/${siteId}/employees/${employeeId}/shifts/offers`, { params })

    const data = response.data.data as EmployeeShiftOfferDto[]

    return {
        offers: data.map((offer) => {
            return employeeShiftOfferFromDto(offer)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}